import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useMemo, useState } from "react";
import {
  AlertDialog,
  AlertDialogClose,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPopup,
  AlertDialogTitle,
  Box,
  Button,
  EntitySelector,
  EntitySelectorContent,
  EntitySelectorEmpty,
  EntitySelectorInput,
  EntitySelectorPool,
  EntitySelectorTrigger,
  Icon,
  Text,
  type BulkAction,
  type EntitySelection,
} from "@salestrak/ui";

import { PageHeader, PageHeaderActions, PageHeaderTitle } from "#app/components/page-header";
import { CustomFieldsTable } from "#app/components/custom-fields/custom-fields-table";
import {
  CustomFieldFormDialog,
  type CustomFieldFormMode,
} from "#app/components/custom-fields/custom-field-form-dialog";
import {
  useFieldTypeSelectorConfig,
  FIELD_TYPE_POOL_ID,
} from "#app/lib/hooks/use-field-type-order";
import { useListQueryState } from "#app/lib/hooks/use-list-query-state";
import { CUSTOM_FIELD_LIST } from "#app/lib/filters/list-configs";
import type { CustomFieldRow, CustomFieldType } from "#app/lib/data";
import { useCustomFieldMutations } from "#app/lib/hooks/use-custom-field-mutations";
import { settleAll } from "#app/lib/utils/settle-all";
import { withPermission } from "#app/components/permissions/require-permission";

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

function CustomFieldsPage() {
  const {
    searchValue,
    setSearchValue,
    sorting,
    setSorting,
    where,
    orderBy,
  } = useListQueryState(CUSTOM_FIELD_LIST);

  const { createCustomField, updateCustomField, deleteCustomField, duplicateCustomField, refetchList } =
    useCustomFieldMutations();

  // --- Create field type selector ---
  const fieldTypeSelectorConfig = useFieldTypeSelectorConfig();

  const handleFieldTypeSelect = useCallback(
    (selections: EntitySelection[]) => {
      const selected = selections[0];
      if (selected) {
        setFormMode({ mode: "create", type: selected.id as CustomFieldType });
      }
    },
    [],
  );

  // --- Form dialog state ---
  const [formMode, setFormMode] = useState<CustomFieldFormMode | null>(null);

  const handleEdit = useCallback((field: CustomFieldRow) => {
    setFormMode({ mode: "edit", field });
  }, []);

  const handleCloseForm = useCallback(() => {
    setFormMode(null);
  }, []);

  // --- Delete dialog ---
  const [deleteTarget, setDeleteTarget] = useState<CustomFieldRow | null>(null);

  const handleDelete = useCallback((field: CustomFieldRow) => {
    setDeleteTarget(field);
  }, []);

  const handleDeleteFromForm = useCallback((field: CustomFieldRow) => {
    setDeleteTarget(field);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!deleteTarget) return;
    await deleteCustomField(deleteTarget.id);
    await refetchList();
    setFormMode(null);
    setDeleteTarget(null);
  }, [deleteTarget, deleteCustomField, refetchList]);

  // --- Duplicate ---
  const handleDuplicate = useCallback(
    async (field: CustomFieldRow) => {
      await duplicateCustomField(field.id);
    },
    [duplicateCustomField],
  );

  // --- Bulk actions ---
  const bulkActions = useMemo<BulkAction<CustomFieldRow>[]>(
    () => [
      {
        id: "delete",
        type: "confirm",
        label: "Delete",
        icon: "trash-2",
        destructive: true,
        confirmTitle: (rows) =>
          `Delete ${rows.length} custom field${rows.length !== 1 ? "s" : ""}?`,
        confirmDescription: (rows) =>
          `This will permanently delete ${rows.length} custom field${rows.length !== 1 ? "s" : ""}. They will no longer be available in any issues or forms. This cannot be undone.`,
        confirmLabel: "Delete fields",
        onAction: async (selected) => {
          await settleAll(selected, (f) => deleteCustomField(f.id));
          await refetchList();
        },
      },
    ],
    [deleteCustomField, refetchList],
  );

  return (
    <Box direction="column" fill="flex">
      <PageHeader centered>
        <PageHeaderTitle size="sm">Custom Fields</PageHeaderTitle>
        <PageHeaderActions>
          <EntitySelector
            config={fieldTypeSelectorConfig}
            value={[]}
            onValueChange={handleFieldTypeSelect}
          >
            <EntitySelectorTrigger
              render={
                <Button size="sm">
                  <Icon name="plus" />
                  <Text>Create field</Text>
                </Button>
              }
            />
            <EntitySelectorContent size="sm">
              <EntitySelectorInput />
              <EntitySelectorPool value={FIELD_TYPE_POOL_ID} />
              <EntitySelectorEmpty>No matching field types</EntitySelectorEmpty>
            </EntitySelectorContent>
          </EntitySelector>
        </PageHeaderActions>
      </PageHeader>

      <CustomFieldsTable
        where={where ?? {}}
        orderBy={orderBy}
        groupByField="none"
        sorting={sorting}
        onSortingChange={setSorting}
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        onEdit={handleEdit}
        onDuplicate={handleDuplicate}
        onDelete={handleDelete}
        bulkActions={bulkActions}
      />

      {/* Create / Edit dialog */}
      <CustomFieldFormDialog
        formMode={formMode}
        onClose={handleCloseForm}
        onCreate={createCustomField}
        onUpdate={updateCustomField}
        onDelete={handleDeleteFromForm}
      />

      {/* Delete confirmation */}
      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}
      >
        <AlertDialogPopup>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleteTarget?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the {deleteTarget?.name} field? It will
              no longer be available to use in any issues or forms. This action
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogClose render={<Button variant="outline" />}>
              <Text>Cancel</Text>
            </AlertDialogClose>
            <Button onClick={confirmDelete}>
              <Text>Delete field</Text>
            </Button>
          </AlertDialogFooter>
        </AlertDialogPopup>
      </AlertDialog>
    </Box>
  );
}

export const Route = createFileRoute("/(app)/$workspace/settings/custom-fields")({
  component: withPermission("custom_fields.manage", CustomFieldsPage),
});
